// Platform state singleton with pub/sub pattern.
//
// Detects whether the device is mobile (touch-primary) or desktop
// (fine pointer) and exposes a reactive `isMobile` flag. Components
// subscribe to changes so they can adjust layout and behavior.
//
// Usage:
//
//   import { platformState } from '../state/platform-state.js';
//
//   connectedCallback() {
//     super.connectedCallback();
//     this._isMobile = platformState.isMobile;
//     this._unsubscribePlatform = platformState.subscribe(() => {
//       this._isMobile = platformState.isMobile;
//     });
//   }
//
//   disconnectedCallback() {
//     super.disconnectedCallback?.();
//     this._unsubscribePlatform?.();
//   }

const PLATFORM_QUERY = '(pointer: coarse)';

class PlatformStateManager {
  constructor() {
    /** @type {Set<() => void>} */
    this.listeners = new Set();

    const mql = window.matchMedia(PLATFORM_QUERY);
    /** @type {boolean} */
    this.isMobile = mql.matches;

    mql.addEventListener('change', (e) => {
      this.isMobile = e.matches;
      this._updateViewport();
      this.notify();
    });

    this._updateViewport();
  }

  /** Disable pinch zoom on mobile to prevent browser zoom during gameplay. */
  _updateViewport() {
    const viewport = document.querySelector('meta[name="viewport"]');
    if (!viewport) return;
    viewport.setAttribute(
      'content',
      this.isMobile
        ? 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no'
        : 'width=device-width, initial-scale=1.0',
    );
  }

  /**
   * Subscribe to platform changes. Returns an unsubscribe function.
   * @param {() => void} fn
   * @returns {() => void}
   */
  subscribe(fn) {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  /** Notify all subscribers of a change. */
  notify() {
    this.listeners.forEach((fn) => {
      fn();
    });
  }
}

export const platformState = new PlatformStateManager();
